import React from "react";

import { useTurnstile, Turnstile } from "react-turnstile";

import { type VerificationService } from "@/lib/types/types";

import { useFlag } from "@/components/FeatureFlags/hooks";
import { MetricIds, useMetrics } from "@/components/FeatureFlags/useMetrics";

import { getQueryOrOption } from "@/lib/ServerApi/TestingRouteOverrides";

import { QUERY_STRING_CAPTCHA_ERROR_OVERRIDE } from "@/constants";

import { setViewModel } from "@/lib/VerificationService/ViewModel";
import { updateFieldValidationErrorsByFieldId } from "@/lib/utils/stepComponentHelpers/stepComponentHelpers";

import {
  CustomDimensionNames,
  CustomMetricNames,
  EventName,
  recordGaEvent,
} from "@/lib/GoogleAnalytics/ga";

import { isCaptchaEnabled } from "@/lib/ProgramTheme/programThemeGetters";

import { CaptchaNotificationComponent, type CaptchaUIState } from "./CaptchaNotificationComponent";

export interface CaptchaComponentProps {
  verificationService: VerificationService;
  isErrored: boolean;
}

const CAPTCHA_RETRY_INTERVAL = 120000; // 2 minutes
const CAPTCHA_LOAD_TIMEOUT = 10000; // 10 seconds

// Page-scoped to dedupe metrics across step-transition remounts (ex: military Active -> Inactive).
const firedMetrics = new Set<string>();

/**
 * CloudFlare Turnstile invisible captcha. Conditionally loads the captcha based on program theme and
 * renders the captcha notification banner (loading, success, or error).
 */
export const CaptchaComponent = ({ verificationService, isErrored }: CaptchaComponentProps) => {
  const turnstile = useTurnstile();
  const captchaEnabledFlag = useFlag("captcha") === "enabled";

  const { programTheme, programId } = verificationService;
  const segment = verificationService.verificationResponse.segment;
  const captchaEnabled = isCaptchaEnabled(programTheme) || captchaEnabledFlag;

  const { trackEvent, recordNumericMetric, flush } = useMetrics({ segment, programId });
  const trackOnce = (metricId: (typeof MetricIds)[keyof typeof MetricIds]) => {
    if (!firedMetrics.has(metricId)) {
      firedMetrics.add(metricId);
      trackEvent(metricId);
    }
  };

  const mockOverride = getQueryOrOption(QUERY_STRING_CAPTCHA_ERROR_OVERRIDE);
  const mockUIState: CaptchaUIState | null =
    mockOverride === "loading" || mockOverride === "success" ? mockOverride : null;
  const mockHasError = !!mockOverride && mockUIState === null;
  const isMocking = !!mockOverride;

  const [uiState, setUIState] = React.useState<CaptchaUIState>("idle");
  const [hasLoaded, setHasLoaded] = React.useState(false);

  // Local source of truth so Turnstile callbacks flip the banner immediately, without waiting on the
  // viewModel/fieldValidationErrors round-trip. Synced with isErrored prop for server rejections.
  const [captchaError, setCaptchaError] = React.useState(isErrored);
  React.useEffect(() => {
    setCaptchaError(isErrored);
  }, [isErrored]);

  const handleRetry = () => {
    trackOnce(MetricIds.CaptchaRetryClicked);
    setUIState("loading");
    turnstile?.reset();
  };

  const updateCaptchaToken = (value: string) => {
    setViewModel({ captchaToken: value });
    updateFieldValidationErrorsByFieldId("captchaToken", value, verificationService);
  };

  const registerCaptchaField = () => {
    const fieldsToSkip = verificationService.viewModel?.fieldsToSkipValidation || [];
    setViewModel({
      fieldsToSkipValidation: fieldsToSkip.filter((id) => id !== "captchaToken"),
    });
    updateCaptchaToken("");
  };

  const unregisterCaptchaField = () => {
    const fieldsToSkip = verificationService.viewModel?.fieldsToSkipValidation || [];
    setViewModel({
      captchaToken: undefined,
      fieldsToSkipValidation: fieldsToSkip.includes("captchaToken")
        ? fieldsToSkip
        : [...fieldsToSkip, "captchaToken"],
    });
  };

  const handleTurnstileVerify = (token: string) => {
    trackOnce(MetricIds.CaptchaVerified);
    // Show success only when transitioning out of a visible loading state (stays hidden on background verifies).
    setUIState((prev) => (prev === "loading" ? "success" : prev));
    setCaptchaError(false);
    updateCaptchaToken(token);
  };

  const handleTurnstileError = (errorCode: string) => {
    if (!hasLoaded) {
      trackOnce(MetricIds.CaptchaErrorScriptLoad);
    } else if (!firedMetrics.has(MetricIds.CaptchaErrorClient)) {
      const code = parseInt(errorCode, 10);
      recordNumericMetric(MetricIds.CaptchaErrorClientCode, isNaN(code) ? 0 : code);
      recordGaEvent({
        eventName: EventName.CAPTCHA_CLIENT_ERROR_CODE,
        params: {
          [CustomMetricNames.ERROR_CODE]: isNaN(code) ? 0 : code,
          [CustomDimensionNames.visibilityState]: document.visibilityState,
        },
      });
      trackOnce(MetricIds.CaptchaErrorClient);
    }
    setUIState("idle");
    setCaptchaError(true);
    updateCaptchaToken("");
  };

  const handleTurnstileUnsupported = () => {
    trackOnce(MetricIds.CaptchaErrorBrowserCompatibility);
    setUIState("idle");
    setCaptchaError(true);
    updateCaptchaToken("");
  };

  // Force a new challenge when captchaToken validation is errored (server rejection or
  // empty token submission). Skip when the script never loaded, reset would hang in "loading".
  React.useEffect(() => {
    if (!captchaEnabled || !isErrored || !hasLoaded) {
      return;
    }

    trackOnce(MetricIds.CaptchaErrorServer);
    setUIState("loading");
    updateCaptchaToken("");
    turnstile?.reset();
  }, [captchaEnabled, isErrored, hasLoaded]);

  // Register captchaToken for form validation, and track abandonment metrics on unmount/page-close.
  React.useEffect(() => {
    if (!captchaEnabled) {
      return;
    }
    // Wait for parent step effects to finish first. They can overwrite the whole viewModel
    // and would clear the captchaToken we just registered (ex: student's CVEC effect).
    const registerTimeout = setTimeout(registerCaptchaField, 0);

    const hasTerminalMetric = () =>
      firedMetrics.has(MetricIds.CaptchaVerified) ||
      firedMetrics.has(MetricIds.CaptchaErrorClient) ||
      firedMetrics.has(MetricIds.CaptchaErrorScriptLoad) ||
      firedMetrics.has(MetricIds.CaptchaErrorBrowserCompatibility) ||
      firedMetrics.has(MetricIds.CaptchaErrorLoadTimeout);

    const trackUnaccounted = () => {
      if (hasTerminalMetric()) {
        return;
      }
      if (!firedMetrics.has(MetricIds.CaptchaRendered)) {
        trackOnce(MetricIds.CaptchaUnmountedBeforeRender);
      } else {
        trackOnce(MetricIds.CaptchaAbandonedAfterRender);
      }
    };

    const onBeforeUnload = () => {
      trackUnaccounted();
      flush();
    };

    window.addEventListener("beforeunload", onBeforeUnload);

    return () => {
      clearTimeout(registerTimeout);
      window.removeEventListener("beforeunload", onBeforeUnload);
      trackUnaccounted();
      unregisterCaptchaField();
    };
  }, [captchaEnabled]);

  // Show the error banner if Turnstile silently fails to load within 10s (script blocked, slow network).
  React.useEffect(() => {
    if (!captchaEnabled || hasLoaded || isMocking) {
      return;
    }
    const timer = setTimeout(() => {
      if (!firedMetrics.has(MetricIds.CaptchaErrorScriptLoad)) {
        trackOnce(MetricIds.CaptchaErrorLoadTimeout);
      }
      // Local state only, updateCaptchaToken would clear field errors set by submit.
      setCaptchaError(true);
    }, CAPTCHA_LOAD_TIMEOUT);

    return () => clearTimeout(timer);
  }, [captchaEnabled, hasLoaded]);

  if (!captchaEnabled) {
    return null;
  }

  return (
    <div>
      {!isMocking && (
        <Turnstile
          sitekey={import.meta.env.VITE_CAPTCHA_SITE_KEY}
          refreshExpired="auto"
          retryInterval={CAPTCHA_RETRY_INTERVAL}
          onLoad={() => {
            setHasLoaded(true);
            trackOnce(MetricIds.CaptchaRendered);
          }}
          onVerify={handleTurnstileVerify}
          onExpire={() => {
            updateCaptchaToken("");
          }}
          onError={handleTurnstileError}
          onUnsupported={handleTurnstileUnsupported}
        />
      )}

      <CaptchaNotificationComponent
        hasError={captchaError || mockHasError}
        uiState={mockUIState ?? uiState}
        onRetry={hasLoaded && uiState === "idle" && captchaError ? handleRetry : undefined}
      />
    </div>
  );
};
